import type { SupabaseClient } from '@supabase/supabase-js';
import { withClient } from './client';
import { checkCacheHealth } from './caches';
import { createFetcherClient, SSEOptions, TypedFetcher } from './Fetcher';
import { consolePanel } from './ConsolePanel';

let _instance: SupabaseUtil | null = null;

export class SupabaseUtil {
  public readonly db: ReturnType<typeof withClient>;
  public readonly fetcher: TypedFetcher;
  private readonly url: string;

  private constructor(client: SupabaseClient, url: string) {
    if (!url) {
      throw new Error('SupabaseUtil requires a URL.');
    }

    this.url = url;
    this.db = withClient(client);
    this.fetcher = createFetcherClient(url);

    consolePanel.panel('🧃 Supabase SDK', ['SDK Loaded v1']);

    checkCacheHealth()
      .then((status) =>
        consolePanel.panel('🧠 Cache', [`Cache Loaded: ${status}`])
      )
      .catch(() =>
        consolePanel.panel('⚠️ Cache', ['Cache Worker failed health check.'])
      );

    this.initNetworkListener();
  }

  static init(client: SupabaseClient, url: string): SupabaseUtil {
    if (_instance) {
      throw new Error(
        `❌ SupabaseUtil already initialized.\n` +
          `🛑 Only one instance allowed.\n` +
          `✅ Use 'SupabaseUtil.instance.db' or 'SupabaseUtil.instance.fetcher' instead.`
      );
    }
    _instance = new SupabaseUtil(client, url);
    return _instance;
  }

  static get instance(): SupabaseUtil {
    if (!_instance) {
      throw new Error(
        `❌ SupabaseUtil not initialized.\n` +
          `⚠️ Call 'SupabaseUtil.init(client, url)' first.`
      );
    }
    return _instance;
  }

  async fetch<T>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    data: Record<string, any> = {},
    params: Record<string, any> = {},
    user?: { userUUID?: string; purchase_id?: string },
    options?: SSEOptions
  ): Promise<T> {
    const requestArgs = {
      method,
      endpoint,
      data,
      params,
      user,
      options,
      url: this.url,
    };

    return this.fetcher.request<T>(requestArgs);
  }

  private initNetworkListener() {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () =>
      consolePanel.panel('🔌 Network', ['Connection restored'])
    );

    window.addEventListener('offline', () =>
      consolePanel.panel('⚡️ Network', ['Connection lost'])
    );
  }
}